import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Modal } from '@clearcover/atlas';
import classnames from 'classnames';

import { FormInput } from 'Shared/components';
import { useAppContext } from 'QuoteApp/services/providers';

import { KeepDriverModal } from '../KeepDriverModal';

import styles from './styles.module.css';
import { stringToBoolean } from 'Shared/utils';
import { FormValuesType } from 'QuoteApp/services/endpoints/GetDetails';
import { useEdit } from 'QuoteApp/utils/contexts';

type DriverRemovalModalProps = {
  onSubmit: (data: any, canBeRemoved?: boolean) => void;
  close: () => void;
  isSubmitting: boolean;
  error?: string;
  driverLabel: string;
  driverId: string;
  setFieldValue?: (field: string, value: any, shouldValidate?: boolean) => void;
  values?: FormValuesType;
  isSubmittingUpdate?: boolean;
};

export const DriverRemovalModal: FC<DriverRemovalModalProps> = ({
  close,
  onSubmit,
  isSubmitting,
  error,
  driverLabel,
  driverId,
  setFieldValue,
  values,
  isSubmittingUpdate,
}) => {
  const { config } = useAppContext();
  const currentDriver = values?.drivers[driverId];

  const getBooleanOrNull = useCallback(
    (
      fieldName:
        | 'livesInHousehold'
        | 'hasAccessToVehicles'
        | 'hasOwnInsurancePolicy'
    ) =>
      currentDriver?.[fieldName]
        ? stringToBoolean(currentDriver[fieldName])
        : null,
    [currentDriver]
  );

  const [knowDriver, setknowDriver] = useState<boolean>();
  const [driverBeenAdded, setDriverBeenAdded] = useState<boolean>();
  const [livesInHouseHold, setLivesInHouseHold] = useState<boolean | null>(
    getBooleanOrNull('livesInHousehold')
  );
  const [hasAccessToVehicles, setHasAccessToVehicles] = useState<
    boolean | null
  >(getBooleanOrNull('hasAccessToVehicles'));
  const [driverHasOwnInsurance, setDriverHasOwnInsurance] = useState<
    boolean | null
  >(getBooleanOrNull('hasOwnInsurancePolicy'));
  const [reason, setReason] = useState<string>('');

  const { changeEditingId } = useEdit();

  const livesInHouseHoldRequired =
    config?.drivers?.statusDetails?.livesInHousehold?.requiredForRemoval ??
    false;
  const hasAccessToVehiclesRequired =
    config?.drivers?.statusDetails?.hasAccessToVehicles?.requiredForRemoval ??
    false;
  const hasOwnInsurancePolicyRequired =
    config?.drivers?.statusDetails?.hasOwnInsurancePolicy?.requiredForRemoval ??
    false;

  const requiredExtraQuestions =
    knowDriver &&
    driverBeenAdded === false &&
    (livesInHouseHoldRequired ||
      hasAccessToVehiclesRequired ||
      hasOwnInsurancePolicyRequired);

  const reasonRequired =
    !livesInHouseHoldRequired &&
    !hasAccessToVehiclesRequired &&
    !hasOwnInsurancePolicyRequired;

  useEffect(() => {
    if (!knowDriver) {
      setDriverBeenAdded(undefined);
      setLivesInHouseHold(getBooleanOrNull('livesInHousehold'));
      setHasAccessToVehicles(getBooleanOrNull('hasAccessToVehicles'));
      setDriverHasOwnInsurance(getBooleanOrNull('hasOwnInsurancePolicy'));
    } else if (driverBeenAdded) {
      setLivesInHouseHold(getBooleanOrNull('livesInHousehold'));
      setHasAccessToVehicles(getBooleanOrNull('hasAccessToVehicles'));
      setDriverHasOwnInsurance(getBooleanOrNull('hasOwnInsurancePolicy'));
    }

    if (requiredExtraQuestions) {
      changeEditingId(driverId);
    } else {
      changeEditingId(null);
    }
  }, [
    changeEditingId,
    driverBeenAdded,
    driverId,
    getBooleanOrNull,
    knowDriver,
    requiredExtraQuestions,
  ]);

  const handleConfirmRemoval = (openModal: () => void) => () => {
    if (knowDriver === false || (knowDriver && driverBeenAdded)) {
      onSubmit(
        {
          statusDetails: {
            livesInHousehold: false,
            hasAccessToVehicles: false,
            hasOwnInsurancePolicy: false,
          },
        },
        true
      );
    } else if (
      requiredExtraQuestions &&
      livesInHouseHold === false &&
      hasAccessToVehicles === false &&
      (driverHasOwnInsurance || driverHasOwnInsurance === false)
    ) {
      onSubmit(
        {
          statusDetails: {
            livesInHousehold: livesInHouseHold,
            hasAccessToVehicles: hasAccessToVehicles,
            hasOwnInsurancePolicy: driverHasOwnInsurance,
          },
        },
        true
      );
    } else if (reasonRequired) {
      onSubmit(
        {
          removalReason: reason,
        },
        true
      );
    } else {
      setFieldValue?.(
        `drivers.${driverId}.livesInHousehold`,
        livesInHouseHold?.toString()
      );
      setFieldValue?.(
        `drivers.${driverId}.hasAccessToVehicles`,
        hasAccessToVehicles?.toString()
      );
      setFieldValue?.(
        `drivers.${driverId}.hasOwnInsurancePolicy`,
        driverHasOwnInsurance?.toString()
      );
      onSubmit({}, false);
      openModal();
    }
  };

  const removeButtonDisabled = () => {
    if (reasonRequired && reason) {
      return false;
    } else if (
      !reasonRequired &&
      (knowDriver === false || (knowDriver && driverBeenAdded))
    ) {
      return false;
    } else if (
      !reasonRequired &&
      requiredExtraQuestions &&
      livesInHouseHold !== null &&
      hasAccessToVehicles !== null &&
      driverHasOwnInsurance !== null
    ) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <div
      data-testid="driver-removal-modal-container"
      className={classnames('fs-mask', styles.driverRemovalContainer)}
    >
      <h1 className={styles.heading}>Confirm Removal of {driverLabel}</h1>
      <p className={styles.description}>
        Please confirm the following details before {driverLabel} is removed.
      </p>
      {reasonRequired ? (
        <FormInput
          variant="selectAutocomplete"
          name="driver_removal"
          data-testid="driver-removal-reason"
          label="Why are you removing this driver?"
          placeholder="Select a reason"
          value={reason}
          options={{
            ...config?.drivers.removalReason,
          }}
          onChange={(value: string) => {
            setReason(value);
          }}
        />
      ) : (
        <>
          <FormInput
            variant="radioButtonGroup"
            name="know_driver"
            data-testid="know-driver"
            label="Is this driver known to the applicant?"
            value={knowDriver?.toString() || ''}
            onChange={(value: string) => {
              setknowDriver(stringToBoolean(value));
            }}
            options={{ true: 'Yes', false: 'No' }}
          />
          {knowDriver && (
            <FormInput
              variant="radioButtonGroup"
              name="driver_been_added"
              data-testid="driver-been-added"
              label="Has this driver already been added to the policy?"
              value={driverBeenAdded?.toString() || ''}
              onChange={(value: string) => {
                setDriverBeenAdded(stringToBoolean(value));
              }}
              options={{ true: 'Yes', false: 'No' }}
            />
          )}
        </>
      )}
      {livesInHouseHoldRequired && requiredExtraQuestions && (
        <FormInput
          variant="radioButtonGroup"
          name="driver_live_in_household"
          data-testid="driver-live-in-household"
          label="Does this driver live in the household?"
          value={livesInHouseHold?.toString() || ''}
          onChange={(value: string) => {
            setLivesInHouseHold(stringToBoolean(value));
          }}
          options={{ true: 'Yes', false: 'No' }}
        />
      )}
      {hasAccessToVehiclesRequired && requiredExtraQuestions && (
        <FormInput
          variant="radioButtonGroup"
          name="driver_access_to_vehicle"
          data-testid="driver-access-to-vehicle"
          label="Does this driver regularly drive, or have regular access to, any of your vehicles?"
          value={hasAccessToVehicles?.toString() || ''}
          onChange={(value: string) => {
            setHasAccessToVehicles(stringToBoolean(value));
          }}
          options={{ true: 'Yes', false: 'No' }}
        />
      )}
      {hasOwnInsurancePolicyRequired && requiredExtraQuestions && (
        <FormInput
          variant="radioButtonGroup"
          name="driver_has_own_insurance"
          data-testid="driver-has-own-insurance"
          label="Does this driver have their own insurance policy with Clearcover or another company?"
          value={driverHasOwnInsurance?.toString() || ''}
          onChange={(value: string) => {
            setDriverHasOwnInsurance(stringToBoolean(value));
          }}
          options={{ true: 'Yes', false: 'No' }}
        />
      )}
      <div className={styles.buttonContainer}>
        <Button
          block
          data-testid="driver-removal-cancel-button"
          variant="secondary"
          size="md"
          onClick={close}
        >
          Cancel
        </Button>
        <Modal
          variant="normal"
          showCloseIcon={false}
          toggle={(openModal) => (
            <Button
              block
              data-testid="driver-removal-confirm-button"
              variant="danger"
              size="md"
              isSubmitting={isSubmitting || isSubmittingUpdate}
              onClick={handleConfirmRemoval(openModal)}
              disabled={removeButtonDisabled()}
            >
              Confirm Removal
            </Button>
          )}
        >
          {(closeKeepDriverModal) => (
            <KeepDriverModal
              close={() => {
                closeKeepDriverModal();
                close();
              }}
              driverLabel={driverLabel}
              isSubmiting={!!isSubmittingUpdate}
            />
          )}
        </Modal>
      </div>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
};
